import {
  Box,
  Tabs,
  TabList,
  Tab,
  Heading,
  Text,
  Divider,
  Skeleton,
  Flex,
} from '@chakra-ui/react';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  SettingsBreadcrumb,
  SettingsFacilitiesBreadcrumb,
} from './SettingsBreadcrumbs';
import { colors } from 'packages/common-frontend/colors';
import { Breadcrumbs } from 'packages/common-frontend/components/Breadcrumbs';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';

export enum SettingsFacilityBorrowingBaseTabOptionKeys {
  loantape = 'loantape',
  configuration = 'configuration',
  sandbox = 'sandbox',
}
type SettingsFacilityBorrowingBaseTabType = {
  key: SettingsFacilityBorrowingBaseTabOptionKeys;
  label: string;
  route: string;
};
const settingsFacilityBorrowingBaseTabOptions: Record<
  SettingsFacilityBorrowingBaseTabOptionKeys,
  SettingsFacilityBorrowingBaseTabType
> = {
  configuration: {
    key: SettingsFacilityBorrowingBaseTabOptionKeys.configuration,
    label: 'Configuration',
    route: '/settings/facilities/borrowing-base/configuration',
  },
  sandbox: {
    key: SettingsFacilityBorrowingBaseTabOptionKeys.sandbox,
    label: 'Sandbox',
    route: '/settings/facilities/borrowing-base/sandbox',
  },
  loantape: {
    key: SettingsFacilityBorrowingBaseTabOptionKeys.loantape,
    label: 'Loan Tape',
    route: '/settings/facilities/borrowing-base/loantape',
  },
};

export const SettingsFacilityBorrowingBaseTemplate: React.FC<{
  creditFacilityId: string;
  selectedTab: SettingsFacilityBorrowingBaseTabOptionKeys;
  children: React.ReactNode;
}> = withLDConsumer()(({ creditFacilityId, selectedTab, children, flags }) => {
  const history = useHistory();

  const enableLenderBaseFieldEdits = !!flags?.enableLenderBaseFieldEdits;

  const {
    data: creditFacilityData,
    loading: isCreditFacilityLoading,
  } = useGetCreditFacilityQuery({ variables: { creditFacilityId } });

  if (!creditFacilityData || isCreditFacilityLoading) {
    return (
      <Skeleton
        h="600px"
        data-testid="settings-facility-borrowing-base-template-loading"
      />
    );
  }

  const creditFacilityName = commonUtils.creditFacilitySlugToName(
    creditFacilityData.creditFacility.slug,
  );

  // can configure feature flags for specific tabs as needed in the future
  // to conditionally render tabs
  const tabs = [
    settingsFacilityBorrowingBaseTabOptions.configuration,
    settingsFacilityBorrowingBaseTabOptions.sandbox,
  ];

  if (enableLenderBaseFieldEdits) {
    // add settingsFacilityBorrowingBaseTabOptions.loantape at the top of tabs.
    tabs.unshift(settingsFacilityBorrowingBaseTabOptions.loantape);
  }

  const breadcrumbs = [
    SettingsBreadcrumb,
    SettingsFacilitiesBreadcrumb,
    {
      label: creditFacilityName,
    },
    {
      label: 'Borrowing Base',
    },
  ];

  return (
    <Box p="40px">
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Box mt="16px">
        <Heading as="h1" mt="16px" mb="16px" fontSize="36px" fontWeight={600}>
          Borrowing Base
        </Heading>
        <Flex>
          <Text fontSize="16px" mb="16px" color={colors.black[70]}>
            Configure the borrowing base mechanics for&nbsp;
          </Text>
          <Text fontSize="16px" mb="16px" fontWeight={500}>
            {`${creditFacilityName}.`}
          </Text>
        </Flex>
        <Divider />
      </Box>

      <Tabs
        variant="line"
        index={tabs.findIndex(({ key }) => key === selectedTab)}
        colorScheme="blue"
        background="white"
      >
        <TabList>
          {tabs.map(({ route, label, key }) => {
            const queryParams = `?${creditFacilityIdQueryParam}=${creditFacilityId}`;
            const tabRedirectRoute = `${route}${queryParams}`;

            return (
              <Tab
                key={key}
                onClick={() => {
                  history.push(tabRedirectRoute);
                }}
                fontSize="sm"
              >
                {label}
              </Tab>
            );
          })}
        </TabList>
      </Tabs>
      <Box mt="32px">{children}</Box>
    </Box>
  );
});
